import { publicResource } from "./visibility";

// Resource article registry.
//
// Any module dropped into ./articles/*.js that `export default`s an article
// object is auto-registered (webpack require.context) — no edits here per
// article. Article shape:
//
//   {
//     slug: string,              // URL: /resources/<slug>
//     cluster: keyof clusters,   // "data" | "seller" | "agent" | "compare"
//     title: string,
//     metaTitle?: string,        // defaults to `${title} | Listino`
//     description: string,       // meta description / hub blurb
//     audience?: string,         // e.g. "Sellers & agents"
//     funnelStage?: string,      // "TOFU" | "MOFU" | "BOFU"
//     datePublished: string,     // ISO date
//     dateModified: string,      // ISO date
//     readingTime?: string,
//     tldr?: Span[] | string,    // answer-first summary
//     blocks: Block[],           // body (see content/richText.js)
//     faqs?: { question: string, answer: Block[] }[],
//     related?: string[],        // slugs
//     primaryCtaPath?: string,   // defaults to "/get-started"
//   }

/**
 * @typedef {Object} ResourceArticle
 * @property {string} slug
 * @property {string} cluster
 * @property {string} title
 * @property {string} description
 * @property {string} datePublished
 * @property {string} dateModified
 * @property {string} [metaTitle]
 * @property {string} [audience]
 * @property {string} [funnelStage]
 * @property {string} [readingTime]
 * @property {string} [image]
 * @property {boolean} [draft]
 * @property {string | import("../richText").Span[]} [tldr]
 * @property {import("../richText").RichBlock[]} blocks
 * @property {{question: string, answer: import("../richText").RichBlock[]}[]} [faqs]
 * @property {Record<string, unknown>[]} [extraJsonLd]
 * @property {string[]} [related]
 * @property {string} [primaryCtaPath]
 */
/** @typedef {{default: ResourceArticle}} ResourceModule */
/** @typedef {((key: string) => ResourceModule) & {keys: () => string[]}} WebpackContext */
/**
 * @typedef {NodeRequire & {context: (directory: string, useSubdirectories: boolean, regExp: RegExp) => WebpackContext}} WebpackRequire
 */

const webpackRequire = /** @type {WebpackRequire} */ (require);

const ctx = webpackRequire.context("./articles", false, /\.js$/);

// Load every article module (including drafts), sort by dateModified
// (newest first), and dedupe by `slug` so duplicate modules can't cause
// repeated entries in the public `resources` listing.
const _loadedModules = ctx
  .keys()
  .map((key) => ctx(key).default)
  .filter(Boolean);

export const allResources = /** @type {any[]} */ (_loadedModules)
  .sort((/** @type {any} */ a, /** @type {any} */ b) =>
    (b.dateModified || "").localeCompare(a.dateModified || "")
  )
  .reduce(
    (/** @type {any[]} */ acc, /** @type {any} */ article) => {
      if (!acc.find((/** @type {any} */ a) => a.slug === article.slug)) acc.push(article);
      return acc;
    },
    /** @type {any[]} */ ([])
  );

// Published articles only — the sole source for hub listings and public
// routes. Drafts stay in allResources for build-time validation, but cannot be
// opened by guessing a direct URL.
export const resources = allResources.filter((/** @type {any} */ a) => !a.draft);

export const resourcesBySlug = allResources.reduce(
  (/** @type {Record<string, any>} */ acc, /** @type {any} */ article) => {
    acc[article.slug] = article;
    return acc;
  },
  /** @type {Record<string, ResourceArticle>} */ ({})
);

/** @param {string} slug */
export const getResource = (slug) => {
  return publicResource(resourcesBySlug[slug]);
};

/** @param {string} clusterKey */
export const resourcesByCluster = (clusterKey) => resources.filter((a) => a.cluster === clusterKey);
